// The places stream's kit, rooms and water on the streamed valley (phase C joined, seams 2 and 3):
//   - the kit: the layout's buildings, props and ruins go through @rpg/meshkit's `createKit` over the kit-pieces table
//     (a camp's fire ring, the dais, the signpost and the notice board are the spike's props: props.ts); their
//     colliders (never the tier's) ride in their chunk's one fixed body, so they load, unload and hash with it;
//   - the rooms: every house the kit gives a room becomes a seamless interior placed where the layout stands it, for
//     the interiors director in the sim (src/places/module.ts) and its view (places-view.ts);
//   - the water: the layout's brook and pond as @rpg/collision water shapes (a channel whose surface falls along the
//     course, a polygon), fitted to the ground @rpg/world carved for them: each shape reaches the line where the
//     sampled ground (the heightfield the physics and the chunk meshes share) rises above the water, plus a margin,
//     so wherever water shows above the ground the query answers it, and the sheet drawn inside the shape is hidden
//     under the bank past it.
import type { ColliderSpec, WaterVolume } from "@rpg/collision";
import { waterChannel, waterPolygon } from "@rpg/collision";
import { createKit, placeInterior, type Kit, type KitPiece, type KitPlacement, type KitTier } from "@rpg/meshkit";
import * as THREE from "three";
import type { GroundPatch, InteriorSpec, Placement, World } from "@rpg/world";
import type { SpikeContent } from "../content";
import { placesContent, type PlacesContent } from "../places/content";
import { buildProp, isProp } from "./props";

/** The kit on the valley: built pieces cached per piece, seed and tier. */
export interface WorldKit {
  content: PlacesContent;
  kit: Kit;
  /** A placed piece built once per tier (cached). */
  piece(name: string, seed: number, tier: KitTier): KitPiece;
  /** A placement's colliders (the kit's, or a prop's), in its own frame, for @rpg/world's chunk bodies. */
  colliders(p: Placement): ColliderSpec[];
  /** Whether a piece is the kit's (else a prop). */
  isKit(name: string): boolean;
  /** A placement's floor in its own frame (improvement step 3, IMP-0026): a house's room (the kit's interior bounds),
   *  a bridge's deck (its span by its width, answering only on the deck, not in the bed under it); null for the rest. */
  footprint(p: Placement): Omit<GroundPatch, "surface"> | null;
  /** A placement's ground patches for @rpg/world: its footprint tagged by `grounds` (the layout's surface per kit
   *  piece); none for a piece it doesn't name. */
  grounds(p: Placement, grounds: Readonly<Record<string, string>> | undefined): GroundPatch[];
}

/** How far under a bridge's ends (m) its deck still answers a foot: the ground at the abutments, not the bed under it. */
export const DECK_BELOW = 0.4;

/** The kit over the content's piece table and the valley's palette with the kit's colours. */
export function createWorldKit(content: SpikeContent): WorldKit {
  const pc = placesContent(content);
  // the kit's material is the view's (kitSurface); the sim only reads geometry-free parts (colliders, rooms)
  const kit = createKit({ defs: pc.defs, palette: pc.palette, material: new THREE.MeshBasicMaterial() });
  const cache = new Map<string, KitPiece>();
  const piece = (name: string, seed: number, tier: KitTier): KitPiece => {
    const k = `${name}:${seed}:${tier}`;
    let p = cache.get(k);
    if (!p) cache.set(k, (p = kit.build(name, seed, tier)));
    return p;
  };
  const names = new Set(kit.names());
  const footprint = (p: Placement): Omit<GroundPatch, "surface"> | null => {
    if (!names.has(p.piece)) return null;
    const def = pc.defs[p.piece] as { kind: string; span?: number; width?: number } | undefined;
    if (def?.kind === "bridge" && def.span && def.width) return { x0: -def.width / 2, z0: -def.span / 2, x1: def.width / 2, z1: def.span / 2, above: -DECK_BELOW };
    const room = piece(p.piece, p.seed ?? 0, "low").interior?.bounds;
    return room ? { x0: room.min.x, z0: room.min.z, x1: room.max.x, z1: room.max.z } : null;
  };
  return {
    content: pc,
    kit,
    piece,
    isKit: (n) => names.has(n),
    footprint,
    grounds: (p, grounds) => {
      const surface = grounds?.[p.piece];
      const f = surface ? footprint(p) : null;
      return f && surface ? [{ ...f, surface }] : [];
    },
    colliders: (p) => {
      if (isProp(p.piece)) return buildProp(p.piece, p.seed ?? 0).colliders;
      // colliders never depend on the tier (the kit draws them from the shape's stream): the low tier's are everyone's
      return piece(p.piece, p.seed ?? 0, "low").colliders.map((c) => ({ ...c, layer: "solid" }));
    },
  };
}

/** A room in the valley: its placement, the kit piece, where it stands, its owner number in the kit batches. */
export interface WorldRoom {
  id: string;
  owner: number;
  piece: KitPiece;
  place: KitPlacement;
  spec: InteriorSpec;
}

/** Every kit placement's owner number for the cut-away (unique across the valley: the batches are per chunk). */
export function kitOwners(world: World, kit: WorldKit): Map<string, number> {
  const out = new Map<string, number>();
  let n = 0;
  for (const p of world.placements()) if (kit.isKit(p.piece) && (p.kind === "building" || p.kind === "prop" || p.kind === "interactable")) out.set(p.id, ++n);
  return out;
}

/** The valley's rooms: every placed kit piece with a room, as a seamless interior. */
export function worldRooms(world: World, kit: WorldKit, owners: Map<string, number>): WorldRoom[] {
  const out: WorldRoom[] = [];
  for (const p of world.placements()) {
    const owner = owners.get(p.id);
    if (!owner) continue;
    const piece = kit.piece(p.piece, p.seed, "low");
    if (!piece.interior) continue;
    const place: KitPlacement = { x: p.pos[0], y: p.y, z: p.pos[1], yaw: p.yaw };
    const w = placeInterior(piece.interior, place);
    out.push({ id: p.id, owner, piece, place, spec: { id: p.id, mode: "seamless", bounds: w.bounds, profile: piece.interior.profile, light: piece.interior.light } });
  }
  return out;
}

/** The ground as the heightfield samples it (bilinear between the layout's cells): what the physics and the chunk
 *  meshes show, which can sit a little off @rpg/world's smooth height function between samples. */
export function sampledGround(world: World): (x: number, z: number) => number {
  const cell = world.layout.terrain.cell;
  const { x0, z0 } = world.layout.bounds;
  return (x, z) => {
    const fx = (x - x0) / cell;
    const fz = (z - z0) / cell;
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const tx = fx - ix;
    const tz = fz - iz;
    const h = (i: number, j: number): number => world.heightAt(x0 + i * cell, z0 + j * cell);
    const a = h(ix, iz) + (h(ix + 1, iz) - h(ix, iz)) * tx;
    const b = h(ix, iz + 1) + (h(ix + 1, iz + 1) - h(ix, iz + 1)) * tx;
    return a + (b - a) * tz;
  };
}

/** How far the water's shapes reach past the line where the ground rises above the water (m). */
export const WATER_FIT_MARGIN = 0.6;

/** The layout's water as @rpg/collision water volumes fitted to the carved ground (see the file's header). */
export function worldWater(world: World, o: { margin?: number; step?: number } = {}): WaterVolume[] {
  const ground = sampledGround(world);
  const margin = o.margin ?? WATER_FIT_MARGIN;
  const step = o.step ?? 0.2;
  const out: WaterVolume[] = [];
  /** The distance from (x, z) along (dx, dz) to where the ground rises to `surface` (capped at `max`). */
  const reach = (x: number, z: number, dx: number, dz: number, surface: number, max: number): number => {
    for (let d = 0; d <= max; d += step) if (ground(x + dx * d, z + dz * d) >= surface) return d;
    return max;
  };
  for (const w of world.water()) {
    const src = world.layout.water.find((v) => v.id === w.id);
    const bank = src?.bank ?? 2;
    if (w.kind === "stream") {
      const pts = w.points;
      const max = w.width / 2 + bank + 2;
      const fitted = pts.map((p, k) => {
        const a = pts[Math.max(0, k - 1)]!;
        const b = pts[Math.min(pts.length - 1, k + 1)]!;
        const l = Math.hypot(b.x - a.x, b.z - a.z) || 1;
        const nx = -(b.z - a.z) / l;
        const nz = (b.x - a.x) / l;
        // the highest surface of the reaches round it: where the brook falls fast (off the valley's wall) a point's
        // water can be its upstream neighbour's
        let top = p.surface;
        for (let j = Math.max(0, k - 2); j <= Math.min(pts.length - 1, k + 2); j++) top = Math.max(top, pts[j]!.surface);
        const side = Math.max(reach(p.x, p.z, nx, nz, top, max), reach(p.x, p.z, -nx, -nz, top, max));
        return { x: p.x, z: p.z, halfWidth: Math.max(w.width / 2, side + margin), surface: p.surface };
      });
      const depth = Math.max(...pts.map((p) => p.surface - p.bed)) + 0.4;
      out.push(waterChannel(w.id, fitted, depth));
    } else {
      const n = 32;
      const outline: [number, number][] = [];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const dx = Math.cos(a);
        const dz = Math.sin(a);
        const r = reach(w.x, w.z, dx, dz, w.surface, w.r + bank + 2) + margin;
        outline.push([w.x + dx * r, w.z + dz * r]);
      }
      out.push(waterPolygon(w.id, outline, w.surface, w.surface - w.bed + 0.4));
    }
  }
  return out;
}
